import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Badge, Select, Empty, Spinner, fmtBRL, fmtNum } from "@/components/ui/Primitives";
import { api, formatErr, fmtDate } from "@/lib/api";
import { Plus, Eye, Truck, Trash2, Search, Pencil, Unlock } from "lucide-react";

export default function ShipmentsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [unidade, setUnidade] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.shipment_status = status;
      const { data } = await api.get("/shipments", { params });
      setItems(data);
    } catch (e) { setErr(formatErr(e)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Unidades da saída: usa o campo `unidades` do backend (join com invoices).
  // Fallback: extrai de itens_calc[i].unidade.
  const unidadesDe = (s) => {
    if (Array.isArray(s.unidades) && s.unidades.length) return s.unidades;
    const set = new Set();
    (s.itens_calc || []).forEach((it) => {
      const u = (it.unidade || "").trim();
      if (u) set.add(u);
    });
    return [...set];
  };
  const unidadesDisponiveis = useMemo(() => {
    const set = new Set();
    items.forEach((s) => unidadesDe(s).forEach((u) => set.add(u)));
    return [...set].sort((a, b) => a.localeCompare(b, "pt-BR"));
  }, [items]);

  const filtered = useMemo(() => {
    let arr = items;
    if (unidade) {
      arr = arr.filter((s) => unidadesDe(s).includes(unidade));
    }
    if (q) {
      const s = q.toLowerCase();
      arr = arr.filter((i) =>
        (i.cliente || "").toLowerCase().includes(s) ||
        String(i.romaneio_saida_numero || "").includes(s) ||
        String(i.nf_saida_numero || "").toLowerCase().includes(s) ||
        (i.destino || "").toLowerCase().includes(s)
      );
    }
    return arr;
  }, [items, q, unidade]);

  const onDelete = async (id) => {
    if (!window.confirm("Excluir esta saída? Os pallets bipados serão devolvidos ao estoque.")) return;
    try { await api.delete(`/shipments/${id}`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  const onReopen = async (id) => {
    if (!window.confirm(
      "Reabrir esta saída para edição?\n\n" +
      "• O status voltará para RASCUNHO\n" +
      "• A conta a receber será cancelada (se não tiver pagamentos)\n" +
      "• Após editar, você precisará finalizar novamente"
    )) return;
    try { await api.post(`/shipments/${id}/reabrir`); await load(); }
    catch (e) { alert(formatErr(e)); }
  };

  return (
    <Layout
      title="Saídas / Romaneios"
      subtitle="Vendas de cebolas para clientes"
      actions={
        <Link to="/shipments/new">
          <Button data-testid="new-shipment-button"><Plus size={16} /> Nova Saída</Button>
        </Link>
      }
    >
      <Card className="p-4 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <div className="block text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1.5">Buscar</div>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-300" />
              <input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Cliente, romaneio ou destino..."
                className="w-full rounded-md bg-stone-50 border border-stone-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-sm"
                data-testid="shipment-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-3">
            <Select label="Unidade" value={unidade} onChange={(e) => setUnidade(e.target.value)} data-testid="shipment-unidade-filter">
              <option value="">Todas</option>
              {unidadesDisponiveis.map((u) => <option key={u} value={u}>{u}</option>)}
            </Select>
          </div>
          <div className="md:col-span-2">
            <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="shipment-status-filter">
              <option value="">Todos</option>
              <option value="pendente">Pendente</option>
              <option value="rascunho">Rascunho</option>
              <option value="finalizado">Finalizado</option>
              <option value="cancelado">Cancelado</option>
            </Select>
          </div>
          <div className="md:col-span-2 text-right text-xs text-ink-500">{filtered.length} resultado(s)</div>
        </div>
      </Card>

      {err && <div className="text-sm text-danger mb-4">{err}</div>}
      {loading ? (
        <div className="flex justify-center py-20"><Spinner size={28} /></div>
      ) : filtered.length === 0 ? (
        <Card><Empty title="Nenhuma saída encontrada" hint="Crie uma nova saída para começar." /></Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-ink-200">
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-500">
                <th className="px-4 py-3">Romaneio</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3">Unidade</th>
                <th className="px-4 py-3">Destino</th>
                <th className="px-4 py-3 text-right">Caixas</th>
                <th className="px-4 py-3 text-right">Valor Líq.</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Data</th>
                <th className="px-4 py-3 w-1">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => {
                const uns = unidadesDe(i);
                return (
                <tr key={i.id} className="border-b border-ink-200 last:border-0 hover:bg-stone-50 transition-colors" data-testid={`shipment-row-${i.id}`}>
                  <td className="px-4 py-3 font-mono font-semibold">
                    RS-{String(i.romaneio_saida_numero || 0).padStart(4, "0")}
                    {i.nf_saida_numero && <div className="text-[11px] font-normal text-ink-500" data-testid={`shipment-nf-${i.id}`}>NF {i.nf_saida_numero}</div>}
                  </td>
                  <td className="px-4 py-3 text-ink-900">{i.cliente || <span className="text-ink-300 italic">— aguardando cliente —</span>}</td>
                  <td className="px-4 py-3" data-testid={`shipment-unidade-${i.id}`}>
                    {uns.length === 0 ? (
                      <span className="text-ink-300 italic text-xs">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {uns.map((u) => (
                          <span
                            key={u}
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                              /arax/i.test(u) ? "bg-amber-100 text-amber-800" :
                              /matriz|vgs/i.test(u) ? "bg-emerald-100 text-emerald-800" :
                              "bg-stone-100 text-stone-800"
                            }`}
                          >
                            {u}
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-ink-700">{i.destino || "—"}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtNum(i.total_caixas, 0)}</td>
                  <td className="px-4 py-3 font-mono text-right">{fmtBRL(i.valor_liquido)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={
                      i.shipment_status === "finalizado" ? "success"
                      : i.shipment_status === "cancelado" ? "danger"
                      : i.shipment_status === "pendente" ? "warn"
                      : "neutral"
                    }>
                      {i.shipment_status}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-ink-700">{fmtDate(i.created_at)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {i.shipment_status === "pendente" ? (
                      <Link to={`/shipments/${i.id}/edit`}>
                        <button className="p-1.5 rounded-md hover:bg-emerald-100 text-brand-500" title="Preencher cliente e preços" data-testid={`shipment-edit-${i.id}`}>
                          <Pencil size={16} />
                        </button>
                      </Link>
                    ) : (
                      <Link to={`/shipments/${i.id}`}>
                        <button className="p-1.5 rounded-md hover:bg-emerald-100 text-brand-500" title="Abrir" data-testid={`shipment-view-${i.id}`}>
                          <Eye size={16} />
                        </button>
                      </Link>
                    )}
                    {(i.shipment_status === "rascunho" || i.shipment_status === "pendente") && (
                      <button onClick={() => onDelete(i.id)} className="p-1.5 rounded-md hover:bg-red-100 text-danger" title="Excluir" data-testid={`shipment-delete-${i.id}`}>
                        <Trash2 size={16} />
                      </button>
                    )}
                    {i.shipment_status === "finalizado" && (
                      <button onClick={() => onReopen(i.id)} className="p-1.5 rounded-md hover:bg-amber-100 text-warn" title="Reabrir para edição" data-testid={`shipment-reopen-${i.id}`}>
                        <Unlock size={16} />
                      </button>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}
    </Layout>
  );
}
